// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import SectionWrapper, { SectionWrapperStyles } from "@adl/App/Components/Common/SectionWrapper";
import SolutionCard, { ImageSize, TitlePosition } from "@adl/App/Widgets/SolutionWidget/SolutionCard";
import { color, xScreen } from "@adl/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

interface SolutionCardFields {
    fields: {
        cardTitle: string;
        image: string;
        title: string;
        viewTitle: string;
        description: string;
        link: LinkFieldValue;
        openInNewTab: boolean;
    };
}

const enum fields {
    isCondensed = "isCondensed",
    title = "title",
    titleVariant = "titleVariant",
    titlePosition = "titlePosition",
    imageSize = "imageSize",
    cards = "cards",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.isCondensed]: boolean;
        [fields.title]: string;
        [fields.titleVariant]: keyof ThemeTypography;
        [fields.titlePosition]: TitlePosition;
        [fields.imageSize]: ImageSize;
        [fields.cards]: SolutionCardFields[];
    };
}

type Props = OwnProps;

interface SolutionsWidgetStyles {
    container?: GridContainerProps;
    content?: GridItemProps;
    condensedContent?: GridItemProps;
    divier?: InjectableCss;
    sectionWrapperStyles?: SectionWrapperStyles;
    solutionsContainer?: GridContainerProps;
    solutionsContent?: GridItemProps;
}

const solutionsWidgetStyles: SolutionsWidgetStyles = {
    container: {
        gap: 22,
        css: css`
            padding-top: 0;
        `,
    },
    condensedContent: {
        width: [4, 4, 4, 2, 2],
        css: css`
            ${xScreen("lte", "sm")} {
                justify-content: center;
            } 
        `
    },
    content: {
        width: [12, 6, 6, 4, 4],
    },
    divier: {
        css: css`
            width: 100%;
            max-width: calc(100% - 22px);
            margin: 24px auto;
            height: 1px;
            border-top: 1px solid ${color("common.ui17")};
        `,
    },
};

const styles = solutionsWidgetStyles;

const SolutionsWidget: React.FC<Props> = ({
    fields: { title, cards, isCondensed, titlePosition, titleVariant, imageSize },
}) => {
    const links = useGetLinks(cards ?? [], o => o.fields.link);

    if (!cards || cards.length === 0) {
        return null;
    }
    const contentStyles = isCondensed ? styles.condensedContent : styles.content;
    return (
        <SectionWrapper
            title={title}
            extendedStyles={styles.sectionWrapperStyles}
            data-test-selector="Aquifer-SolutionsWidget"
        >
            <GridContainer {...styles.container} className="solutionsWrapper">
                {cards.map((card, index) => (
                    <React.Fragment key={index.toString()}>
                        {!isCondensed && index > 0 && index % 3 === 0 && <StyledWrapper {...styles.divier} />}
                        <GridItem {...contentStyles}>
                            <SolutionCard
                                link={links[index]?.url}
                                openInNewTab={card.fields.openInNewTab}
                                imageSrc={card.fields.image}
                                title={card.fields.title}
                                viewTitle={card.fields.viewTitle}
                                description={card.fields.description}
                                isCondensed={isCondensed}
                                titlePosition={titlePosition}
                                titleVariant={titleVariant}
                                imageSize={imageSize}
                            />
                        </GridItem>
                    </React.Fragment>
                ))}
            </GridContainer>
        </SectionWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: SolutionsWidget,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.isCondensed,
                displayName: "Condensed View",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.titleVariant,
                displayName: "Title Variant",
                editorTemplate: "DropDownField",
                defaultValue: "h4",
                options: [
                    {
                        displayName: "h4",
                        value: "h4",
                    },
                    {
                        displayName: "h3",
                        value: "h3",
                    },
                ],
                fieldType: "General",
                isVisible: (item: HasFields) => !item.fields.isCondensed,
            },
            {
                name: fields.titlePosition,
                displayName: "Title Position",
                editorTemplate: "DropDownField",
                defaultValue: "top",
                options: [
                    {
                        displayName: "Above Image",
                        value: "top",
                    },
                    {
                        displayName: "Under Image",
                        value: "bottom",
                    },
                ],
                fieldType: "General",
                isVisible: (item: HasFields) => !item.fields.isCondensed,
            },
            {
                name: fields.imageSize,
                displayName: "Image Size",
                editorTemplate: "DropDownField",
                defaultValue: "small",
                options: [
                    {
                        displayName: "small",
                        value: "small",
                    },
                    {
                        displayName: "medium",
                        value: "medium",
                    },
                    {
                        displayName: "large",
                        value: "large",
                    },
                ],
                fieldType: "General",
                isVisible: (item: HasFields) => !item.fields.isCondensed,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.cards,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "link",
                        displayName: "Link",
                        editorTemplate: "LinkField",
                        defaultValue: { value: "", type: "Page" },
                        isRequired: true,
                        validate: (link: LinkFieldValue) => {
                            if (!link || !link.value) {
                                return "A value must be provided.";
                            }
                            return null;
                        },
                    },

                    {
                        name: "description",
                        displayName: "Description",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "viewTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewTab",
                        displayName: "Open in New Tab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
